/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import classNames from 'classnames';
import { ProductGridCardActions } from './components/ProductGridCardActions';
import { ProductGridCardContent } from './components/ProductGridCardContent';
import { ProductGridCardImage } from './components/ProductGridCardImage';
import { ProductGridCardPreview } from './components/ProductGridCardPreview';
import { ProductGridCardTag } from './components/ProductGridCardTag';
import { RewardToggle } from './components/RewardToggle';
import { useProductGridCardPreview } from './hooks/useProductGridCardPreview';
import type { ProductGridCardProps } from './types';

export type {
  ProductGridCardCtaPosition,
  ProductGridCardImageFit,
  ProductGridCardImageFrame,
  ProductGridCardPreviewItem,
  ProductGridCardPreviewLayout,
  ProductGridCardPreviewStyle,
  ProductGridCardPrimaryCtaIcon,
  ProductGridCardProps,
  RewardToggleColour,
  RewardToggleLabels,
} from './types';
export {
  getProductGridCardMetrics,
  mapProductToProductGridCardProps,
  getProductGridCardPreviewItems,
} from './helpers';

const ProductGridCard: React.FC<ProductGridCardProps> = ({
  id,
  className,
  tagColor,
  tagText,
  imageUrl,
  imageAlt = 'Card image',
  imageFrame = 'plain',
  imageFit = 'contain',
  title,
  metricPrefix,
  metricPrimary,
  metricSecondary,
  badgeText,
  showBadgeInfoIcon = false,
  previewItems = [],
  previewImageAlt = 'Preview',
  previewStyle = 'icon',
  rewardToggleColour = 'BLUE',
  rewardToggleLabels,
  rewardExpanded,
  onRewardExpandedChange,
  primaryCtaLabel,
  primaryCtaUrl,
  onPrimaryCtaClick,
  primaryCtaPosition = 'footer',
  primaryCtaIcon = 'arrow',
  secondaryCtaLabel,
  secondaryCtaUrl,
  onSecondaryCtaClick,
}) => {
  const { hasPreview, hiddenPreviewCount, isPreviewCard, visiblePreviewItems } =
    useProductGridCardPreview({
      previewItems,
      previewStyle,
    });
  // Reward Toggle (CMS-1857) is opt-in for the Product Card Grid module, signalled by the
  // presence of localized labels. Other consumers (e.g. Reward Card Grid details modal) keep
  // the static preview icon row.
  const showRewardToggle = Boolean(rewardToggleLabels);

  return (
    <article
      className={classNames(
        'bg-fill/card border border-borders/default flex h-full overflow-hidden rounded-[10px]',
        'w-40 md:w-58 lg:w-68',
        'md:rounded-radius/card',
        className,
      )}
      data-product-id={id}
    >
      <div className="flex size-full flex-col gap-2 p-2 md:gap-[10px] md:p-[10px] lg:gap-spacing/gap/soft-12px lg:p-spacing/padding/soft-12px">
        <ProductGridCardTag
          tagColor={tagColor}
          tagText={tagText}
          isPreviewCard={isPreviewCard}
        />

        {imageUrl && (
          <ProductGridCardImage
            imageUrl={imageUrl}
            imageAlt={imageAlt}
            imageFrame={imageFrame}
            imageFit={imageFit}
          />
        )}

        <ProductGridCardActions
          primaryCtaLabel={primaryCtaLabel}
          primaryCtaUrl={primaryCtaUrl}
          onPrimaryCtaClick={onPrimaryCtaClick}
          primaryCtaPosition={primaryCtaPosition}
          primaryCtaIcon={primaryCtaIcon}
          secondaryCtaLabel={secondaryCtaLabel}
          secondaryCtaUrl={secondaryCtaUrl}
          onSecondaryCtaClick={onSecondaryCtaClick}
          placement="afterImage"
        />

        <div className="flex grow flex-col">
          <ProductGridCardContent
            title={title}
            metricPrefix={metricPrefix}
            metricPrimary={metricPrimary}
            metricSecondary={metricSecondary}
            badgeText={badgeText}
            showBadgeInfoIcon={showBadgeInfoIcon}
          />
        </div>

        {/* Rewards live in the bottom group so the Reward Toggle stays anchored directly above the
            View Details button instead of floating under short content; the grow block above holds
            only the content and pushes this whole group to the bottom of the card (CMS-1857). */}
        <div className="mt-auto">
          {hasPreview && rewardToggleLabels && (
            <div className="pb-2 md:pb-[10px] lg:pb-spacing/gap/soft-12px">
              <RewardToggle
                items={previewItems}
                colour={rewardToggleColour}
                labels={rewardToggleLabels}
                previewImageAlt={previewImageAlt}
                expanded={rewardExpanded}
                onExpandedChange={onRewardExpandedChange}
              />
            </div>
          )}
          {hasPreview && !showRewardToggle && (
            <div className="pb-2 md:pb-[10px] lg:pb-spacing/gap/soft-12px">
              <ProductGridCardPreview
                previewImageAlt={previewImageAlt}
                visiblePreviewItems={visiblePreviewItems}
                hiddenPreviewCount={hiddenPreviewCount}
                isPreviewCard={isPreviewCard}
              />
            </div>
          )}
          <ProductGridCardActions
            primaryCtaLabel={primaryCtaLabel}
            primaryCtaUrl={primaryCtaUrl}
            onPrimaryCtaClick={onPrimaryCtaClick}
            primaryCtaPosition={primaryCtaPosition}
            primaryCtaIcon={primaryCtaIcon}
            secondaryCtaLabel={secondaryCtaLabel}
            secondaryCtaUrl={secondaryCtaUrl}
            onSecondaryCtaClick={onSecondaryCtaClick}
            placement="footer"
          />
        </div>
      </div>
    </article>
  );
};

export default ProductGridCard;
